import {
  Col,
  Form,
  Image,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Card,
} from "antd";
import { Fragment } from "react";
import { simpleMusculoskeletalCalculator } from '../utils/Calculators';
import { calculateFormLayoutSpans, evaluateVisibility, isDependentField, buildDependentLookup } from "../utils/formLayouts";
import { backFitnessImages, jointRangesImages } from '../../../../images/simple-musculoskeletal';

const SimpleMusculoskeletalForm = ({
  inputs,
  toDoctor,
  disableForm,
  readOnly = false,
  roles,
  status,
  form,
  patient,
  fromExternal,
  allExams,
  isGroupedExams,
}) => {
  const triggerCalculator = (item, value) => {
    simpleMusculoskeletalCalculator(form, inputs, item, value);
  };

  const isDoctor = roles.includes('doctor') || roles.includes('direccion_medica');
  const isPsychologist = roles.includes('equipo_psicologia');

  const getDisableCondition = (item) => {
    return readOnly || !((isDoctor && toDoctor) || (!disableForm && !isDoctor) || (isDoctor && item.medical_visibility) || (!disableForm && isPsychologist));
  };

  const isItemVisible = (item) => {
    return (!item.blocked && !item.medical_visibility) || (!item.blocked && isPsychologist) || (item.medical_visibility && isDoctor);
  };

  const normalizeOptions = (options, fallback = []) => {
    return Array.isArray(options) ? options : options?.split(',').map(o => o.trim()) || fallback;
  };

  const getInitialValue = (item) => {
    if (item.result) {
      const resultArray = Array.isArray(item.result) ? item.result : item.result.split(',').map(r => r.trim());
      return resultArray[0];
    }
    return item.default_value || undefined;
  };

  const getOptionValue = (opt) => {
    return opt.value ? opt.value.toString()?.trim() : opt.text || opt;
  };

  const renderTotalRow = (rawInputs, totalCode) => {
    const totalItem = rawInputs.find(q => q.code === totalCode);
    if (!totalItem) return null;
    return (
      <Row className="p-15" align="middle">
        <Col span={15} className="text-right pr-10">
          <strong className="simple-musculoskeletal-total-label">TOTAL</strong>
        </Col>
        <Col span={4} className="simple-musculoskeletal-table-score-cell">
          <Form.Item name={['questions', totalItem.id.toString()]} initialValue={totalItem.result || totalItem.default_value} className="mb-0">
            <Input disabled className="text-center" />
          </Form.Item>
        </Col>
        <Col span={5}></Col>
      </Row>
    );
  };

  const renderScoreCell = (mainItem) => (
    <Col span={4} className="simple-musculoskeletal-table-score-cell">
      <Form.Item shouldUpdate noStyle>
        {() => {
          const val = form?.getFieldValue(['questions', mainItem.id.toString()]);
          return <Input disabled value={val} className="text-center" />;
        }}
      </Form.Item>
    </Col>
  );

  const renderImageRadioOptions = ({ row, options, initialValue, disableCondition, mainItem, colSpan }) => (
    <Col span={10}>
      <Form.Item name={['questions', mainItem.id.toString()]} initialValue={initialValue} className="mb-0">
        <Radio.Group className="width-100" disabled={disableCondition} onChange={(e) => triggerCalculator(mainItem, e.target.value)}>
          <Row align="middle">
            {options.map((opt, optIndex) => {
              const val = getOptionValue(opt);
              return (
                <Col span={colSpan} key={val} className="text-center">
                  <Radio.Button value={val} className="image-radio-button">
                    <Image src={row.images[optIndex]} width={80} height={80} preview={false} />
                  </Radio.Button>
                </Col>
              );
            })}
          </Row>
        </Radio.Group>
      </Form.Item>
    </Col>
  );

  const renderLabelWithNormalRange = (text) => {
    if (text && text.includes('(Normal')) {
      const parts = text.split('(Normal');
      return (
        <div>
          <div><strong>{parts[0].trim()}</strong></div>
          <div className="mt-5"><small>(Normal {parts[1]}</small></div>
        </div>
      );
    }
    return <div><strong dangerouslySetInnerHTML={{ __html: text }} /></div>;
  };

  const renderBackFitnessTable = (rawInputs, type) => {
    const tableRows = [
      { label: 'Flexibilidad Fuerza / Abdomen', mainCode: 'FLEXIBILIDAD_ABDOMEN_1_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'OBSERVACIONES_FLEXIBILIDAD_ABDOMEN_2_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', images: backFitnessImages.flexAbdomen },
      { label: 'Cadera', mainCode: 'CADERA_3_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'OBSERVACIONES_CADERA_4_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', images: backFitnessImages.hip },
      { label: 'Muslo', mainCode: 'MUSLO_5_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'OBSERVACIONES_MUSLO_6_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', images: backFitnessImages.thigh },
      { label: 'Abdomen lateral', mainCode: 'ABDOMEN_LATERAL_7_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'OBSERVACIONES_ABDOMEN_LATERAL_8_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE', images: backFitnessImages.lateralAbdomen }
    ];
    const totalCode = 'TOTAL_9_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE';

    return (
      <div className="simple-musculoskeletal-table mb-32">
        <Row className="simple-musculoskeletal-table-header p-15">
          <Col span={5}>APTITUD DE ESPALDA</Col>
          <Col span={10}>
            <Row>
              <Col span={6} className="text-center">Excelente: 1</Col>
              <Col span={6} className="text-center">Promedio: 2</Col>
              <Col span={6} className="text-center">Regular: 3</Col>
              <Col span={6} className="text-center">Pobre: 4</Col>
            </Row>
          </Col>
          <Col span={4} className="text-center">Puntaje</Col>
          <Col span={5} className="text-center">Observaciones</Col>
        </Row>
        {tableRows.map((row) => {
          const mainItem = rawInputs.find(q => q.code === row.mainCode);
          const obsItem = rawInputs.find(q => q.code === row.obsCode);
          if (!mainItem) return null;

          const disableCondition = getDisableCondition(mainItem);
          const normalizedOptions = normalizeOptions(mainItem.options, ['1', '2', '3', '4']);
          const initialValue = getInitialValue(mainItem);

          return (
            <Row key={row.mainCode} className="simple-musculoskeletal-table-row p-15">
              <Col span={5} className="simple-musculoskeletal-table-row-label">
                <div><strong dangerouslySetInnerHTML={{ __html: mainItem.name || row.label }} /></div>
              </Col>
              {renderImageRadioOptions({ row, options: normalizedOptions, initialValue, disableCondition, mainItem, colSpan: 6 })}
              {renderScoreCell(mainItem)}
              <Col span={5} className="simple-musculoskeletal-table-obs-cell">
                 {obsItem && (
                   <Form.Item name={['questions', obsItem.id.toString()]} initialValue={obsItem.result || obsItem.default_value} className="mb-0">
                     <Input.TextArea disabled={disableCondition} onChange={(e) => triggerCalculator(obsItem, e.target.value)} autoSize={{ minRows: 2, maxRows: 4 }} />
                   </Form.Item>
                 )}
              </Col>
            </Row>
          );
        })}
        {renderTotalRow(rawInputs, totalCode)}
      </div>
    );
  };

  const renderJointRangesTable = (rawInputs, type) => {
    const tableRows = [
      { label: 'Abducción de hombro (Normal 0º - 180º)', mainCode: 'ABDUCACION_HOMBRO_0_180_1_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'DOLOR_CONTRA_RESISTENCIA_HOMBRO_180_2_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', images: jointRangesImages.shoulderAbduction180 },
      { label: 'Abducción de hombro (Normal 0º - 60º)', mainCode: 'ABDUCACION_HOMBRO_0_60_3_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'DOLOR_CONTRA_RESISTENCIA_HOMBRO_60_4_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', images: jointRangesImages.shoulderAbduction60 },
      { label: 'Rotación externa (Normal 0º - 60º)', mainCode: 'ROTACION_EXTERNA_5_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'DOLOR_CONTRA_RESISTENCIA_ROTACION_EXTERNA_6_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', images: jointRangesImages.externalRotation },
      { label: 'Rotación interna (Normal 0º - 60º)', mainCode: 'ROTACION_INTERNA_7_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', obsCode: 'DOLOR_CONTRA_RESISTENCIA_8_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE', images: jointRangesImages.internalRotation }
    ];
    const totalCode = 'TOTAL_9_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE';

    return (
      <div className="simple-musculoskeletal-table mb-32">
        <Row className="simple-musculoskeletal-table-header p-15">
          <Col span={5}>RANGOS ARTICULARES</Col>
          <Col span={10}>
            <Row>
              <Col span={8} className="text-center">ÓPTIMO: 1</Col>
              <Col span={8} className="text-center">LIMITADO: 2</Col>
              <Col span={8} className="text-center">MUY LIMITADO: 3</Col>
            </Row>
          </Col>
          <Col span={4} className="text-center">Puntaje</Col>
          <Col span={5} className="text-center">Dolor contra resistencia</Col>
        </Row>
        {tableRows.map((row) => {
          const mainItem = rawInputs.find(q => q.code === row.mainCode);
          const obsItem = rawInputs.find(q => q.code === row.obsCode);
          if (!mainItem) return null;

          const disableCondition = getDisableCondition(mainItem);
          const normalizedOptions = normalizeOptions(mainItem.options, ['1', '2', '3']);
          const initialValue = getInitialValue(mainItem);

          return (
            <Row key={row.mainCode} className="simple-musculoskeletal-table-row p-15">
              <Col span={5} className="simple-musculoskeletal-table-row-label">
                {renderLabelWithNormalRange(mainItem.name || row.label)}
              </Col>
              {renderImageRadioOptions({ row, options: normalizedOptions, initialValue, disableCondition, mainItem, colSpan: 8 })}
              {renderScoreCell(mainItem)}
              <Col span={5} className="simple-musculoskeletal-table-obs-cell">
                 {obsItem && (() => {
                   const obsOptions = normalizeOptions(obsItem.options, ['SI', 'NO']);
                   const obsInit = getInitialValue(obsItem);
                   return (
                     <Form.Item name={['questions', obsItem.id.toString()]} initialValue={obsInit} className="mb-0">
                       <Radio.Group disabled={disableCondition} onChange={(e) => triggerCalculator(obsItem, e.target.value)} options={obsOptions.map((option) => ({
                            label: option.text || option,
                            value: option.value ? option.value.toString()?.trim() : option.text || option,
                          }))}/>
                     </Form.Item>
                   )
                 })()}
              </Col>
            </Row>
          );
        })}
        {renderTotalRow(rawInputs, totalCode)}
      </div>
    );
  };

  const getOrderedInputTypes = () => {
    const defaultOrder = ['measurements', 'procedures', 'surveys'];
    const order = inputs?.extra_data_exam?.input_types_order || defaultOrder;

    let orderedTypes = [];

    order.forEach(typeKey => {
      if (typeKey === 'measurements') {
        orderedTypes.push({ input_name: 'measurements' });
      } else if (typeKey === 'procedures') {
        orderedTypes.push({ input_name: 'procedures' });
      } else if (typeKey === 'surveys') {
        if (inputs.surveys) {
          orderedTypes.push(...inputs.surveys.map(survey => ({ ...survey, input_name: 'questions' })));
        }
      }
    });

    return orderedTypes;
  };

  return (
    <div className={`normal-form-container ${isGroupedExams ? 'is-grouped' : ''}`}>
      {
        getOrderedInputTypes().map((type, typeIndex) => {
          const rawInputs = type.input_name === 'questions'
            ? inputs.questions.filter(question => question.survey_id === type.id)
            : inputs[type.input_name];

          if (!rawInputs || rawInputs.length === 0) return null;

          const itemsWithLayout = calculateFormLayoutSpans(rawInputs, inputs?.form_layout, fromExternal, allExams?.map(exam => exam?.inputs?.form_layout));
          if (!itemsWithLayout.some(isItemVisible)) return null;
          const dependentLookup = buildDependentLookup(inputs?.form_layout);

          const isBackFitness = type.input_name === 'questions' && rawInputs.some(q => q.code === 'FLEXIBILIDAD_ABDOMEN_1_APTITUD_ESPALDA_MUSCULO_ESQUELETICO_SIMPLE');
          const isJointRanges = type.input_name === 'questions' && rawInputs.some(q => q.code === 'ABDUCACION_HOMBRO_0_180_1_RANGOS_ARTICULARES_MUSCULO_ESQUELETICO_SIMPLE');

          if (isBackFitness) {
            return renderBackFitnessTable(rawInputs, type);
          }

          if (isJointRanges) {
            return renderJointRangesTable(rawInputs, type);
          }

          return (
            <Fragment key={typeIndex}>
              <Card
                className="form-section-card mb-32"
                bordered={true}
              >
                <Row gutter={[16, 24]} className="aligned-form">
                  {
                    itemsWithLayout.sort((first, second) => first.order - second.order).map(item => {
                      const disableCondition = getDisableCondition(item);
                      const visible = isItemVisible(item);

                      return (
                        <Fragment key={`${type.input_name}-${item.id}`}>
                          <Form.Item shouldUpdate noStyle>
                            {() => {
                              const layoutVisible = evaluateVisibility(item, type.input_name, form?.getFieldValue, inputs?.form_layout, patient, rawInputs);
                              if (!visible || !layoutVisible) return null;
                              const isDependent = isDependentField(item, dependentLookup);
                              return (
                                <Col {...item.spans}>
                                  <Form.Item
                                    label={
                                      <div dangerouslySetInnerHTML={{ __html: `${item.name}${item.unit && item.unit !== '' ? ` [${item.unit}]` : ''}` }} />
                                    }
                                    className={`${status(item, type)}${isDependent ? ' dependent-field' : ''}`}
                                  >
                                    {(item.input_type === 'select' || item.input_type === 'select_multiple') && (
                                      (() => {
                                        const normalizedOptions = Array.isArray(item.options)
                                          ? item.options
                                          : item.options.split(',').map(opt => opt.trim());

                                        let initialValue;

                                        if (item.result) {
                                          const resultArray = Array.isArray(item.result)
                                            ? item.result
                                            : item.result.split(',').map(v => v.trim());

                                          initialValue = item.input_type === 'select_multiple'
                                            ? resultArray
                                            : resultArray[0];
                                        } else {
                                          const defaultOption = item.default_value ||
                                            (normalizedOptions[0]?.value?.toString() || normalizedOptions[0]);

                                          initialValue = item.input_type === 'select_multiple'
                                            ? (Array.isArray(defaultOption) ? defaultOption : [defaultOption])
                                            : defaultOption;
                                        }

                                        return (
                                          <Form.Item
                                            name={[type.input_name, item.id.toString()]}
                                            initialValue={initialValue}
                                          >
                                            <Select
                                              mode={item.input_type === 'select_multiple' && 'multiple'}
                                              className="cmt-select mt-10"
                                              placeholder={
                                                item.default_value ? item.default_value : `Seleccione ${item.input_type === 'select' ? 'opción' : 'opcion/es'}`
                                              }
                                              disabled={disableCondition}
                                              allowClear
                                              options={normalizedOptions.map((option) => ({
                                                label: option.text || option,
                                                value: option.value ? option.value.toString() : option,
                                              }))}
                                              onChange={(value) => triggerCalculator(item, value)}
                                            />
                                          </Form.Item>
                                        );
                                      })()
                                    )}

                                    {item.input_type === 'text' && (
                                      <Form.Item name={[type.input_name, item.id.toString()]} initialValue={item.result || item.default_value}>
                                        <Input.TextArea
                                          className="cmt-input mt-10 width-100"
                                          placeholder={item.default_value ? item.default_value : 'Ingrese texto'}
                                          rows={4}
                                          disabled={disableCondition}
                                          onChange={(e) => triggerCalculator(item, e.target.value)}
                                        />
                                      </Form.Item>
                                    )}
                                    {item.input_type === 'string' && (
                                      <Form.Item name={[type.input_name, item.id.toString()]} initialValue={item.result || item.default_value}>
                                        <Input
                                          className="cmt-input mt-10 width-100"
                                          placeholder={item.default_value ?? 'Ingrese dato'}
                                          disabled={disableCondition}
                                          onChange={(e) => triggerCalculator(item, e.target.value)}
                                        />
                                      </Form.Item>
                                    )}

                                    {['integer', 'float'].includes(item.input_type) && (
                                      <Form.Item name={[type.input_name, item.id.toString()]} initialValue={item.result || item.default_value}>
                                        <InputNumber
                                          code={item.code}
                                          className="cmt-input-number mt-10 width-100"
                                          placeholder={item.default_value ?? 'Ingrese valor'}
                                          min={(item.restrict_min && item.result) ? item.result - item.restrict_min : null}
                                          max={(item.restrict_max && item.result) ? item.result - item.restrict_max : null}
                                          disabled={disableCondition}
                                          onChange={(value) => triggerCalculator(item, value)}
                                        />
                                      </Form.Item>
                                    )}
                                    {item.input_type === 'radio' && (
                                      (() => {
                                        const normalizedOptions = Array.isArray(item.options)
                                          ? item.options
                                          : item.options.split(',').map(option => option.trim());
                                        let initialValue;

                                        if (item.result) {
                                          const resultArray = Array.isArray(item.result)
                                            ? item.result
                                            : item.result.split(',').map(result => result.trim());

                                          initialValue = resultArray[0];
                                        } else {
                                          initialValue = item.default_value || undefined;
                                        }

                                        return (
                                          <Form.Item name={[type.input_name, item.id.toString()]} initialValue={initialValue}>
                                            <Radio.Group
                                              className="cmt-radio mt-10"
                                              disabled={disableCondition}
                                              onChange={(e) => triggerCalculator(item, e.target.value)}
                                              options={normalizedOptions.map((option) => ({
                                                label: option.text || option,
                                                value: option.value ? option.value.toString()?.trim() : option.text || option,
                                              }))}
                                            />
                                          </Form.Item>
                                        );
                                      })()
                                    )}

                                  </Form.Item>
                                </Col>
                              );
                            }}
                          </Form.Item>
                        </Fragment>
                      );
                    })
                  }
                </Row>
              </Card>
            </Fragment>
          );
        })
      }
    </div>
  );
};

export default SimpleMusculoskeletalForm;
